import { prefersReducedMotion, onDocumentReady, throttle } from '@theme/utilities';

const SELECTOR = '[data-parallax-section]';
const BG_SELECTOR = '[data-parallax-bg]';

/**
 * @param {HTMLElement} section
 */
function setupParallax(section) {
  const bg = section.querySelector(BG_SELECTOR) || section.querySelector('.parallax-banner__bg');
  if (!bg) return;

  const speedAttr = section.getAttribute('data-parallax-speed');
  const speed = speedAttr ? Number(speedAttr) : 0.25;

  if (prefersReducedMotion() || !Number.isFinite(speed) || speed <= 0) {
    section.style.setProperty('--parallax-y', '0px');
    return;
  }

  /** @type {number | null} */
  let rafId = null;

  const update = () => {
    rafId = null;

    const rect = section.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    if (viewportH <= 0) return;

    // Progress through viewport: -1 (above) -> 1 (below)
    const progress = (rect.top + rect.height / 2 - viewportH / 2) / (viewportH / 2);

    // Translate opposite scroll direction; clamp for stability
    const clamped = Math.max(-1.5, Math.min(1.5, progress));
    const maxShift = Math.min(200, rect.height * 0.25);
    const y = -clamped * maxShift * speed;

    section.style.setProperty('--parallax-y', `${y.toFixed(2)}px`);
  };

  const requestUpdate = () => {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(update);
  };

  const onScroll = throttle(requestUpdate, 16);
  const onResize = throttle(requestUpdate, 50);

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  // Initial paint + when image loads (to account for layout shifts)
  requestUpdate();

  // Expose cleanup for theme editor section unload patterns (if used)
  section.__parallaxCleanup = () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = null;
  };
}

function initAll() {
  document.querySelectorAll(SELECTOR).forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    if (section.dataset.parallaxInitialized === 'true') return;
    section.dataset.parallaxInitialized = 'true';
    setupParallax(section);
  });
}

onDocumentReady(() => {
  initAll();

  // Shopify theme editor events
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    target.querySelectorAll(SELECTOR).forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      section.dataset.parallaxInitialized = 'false';
    });
    initAll();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    target.querySelectorAll(SELECTOR).forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      section.__parallaxCleanup?.();
    });
  });
});

